/**
 * Cohorts Expanded Tab
 * V6.15 V2: Full cohort retention heatmap with tier breakdown
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Skeleton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem,
} from '@mui/material';
import apiService from '../../../services/api';

interface CohortMatrix {
  cohort: string;
  cohortSize: number;
  months: Record<number, number>;
}

interface TierCohort {
  cohort: string;
  tier: string;
  cohortSize: number;
  stillActive: number;
  retentionPct: number | null;
  isSufficient: boolean;
}

interface CohortData {
  /** The window the SERVER actually used, in whole months. May be wider than
   *  the pill implies — cohorts cannot be measured in part-months. */
  months?: number;
  matrix: CohortMatrix[];
  byTier: TierCohort[];
}

// Industry benchmarks for SaaS
const INDUSTRY_BENCHMARK = [100, 68, 55, 48, 42, 38, 35, 33, 31, 29, 28, 27];
const LIFESCRIBE_TARGET = [100, 80, 72, 66, 62, 58, 55, 53, 51, 50, 49, 48];

const heatmapColor = (pct: number): string => {
  if (pct >= 80) return '#0d9488';
  if (pct >= 60) return '#14b8a6';
  if (pct >= 40) return '#5eead4';
  if (pct >= 20) return '#99f6e4';
  if (pct > 0) return '#ccfbf1';
  return '#F9FAFB';
};

const heatmapTextColor = (pct: number): string => {
  if (pct >= 60) return '#ffffff';
  if (pct >= 20) return '#134e4a';
  return '#6B7280';
};

const formatCohort = (c: string): string => {
  const d = new Date(c + 'T00:00:00');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
};

interface CohortsExpandedTabProps {
  timeRangeDays?: string;
}

const CohortsExpandedTab: React.FC<CohortsExpandedTabProps> = ({ timeRangeDays }) => {
  const [data, setData] = useState<CohortData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState<string>('all');
  // Math.round sent 0 for every window under ~15 days, which the server then
  // floored — so Today/3D/7D/14D/30D all returned the same matrix. Round UP:
  // a sub-month window is answered with the one month that contains it.
  const requestedDays = parseInt(timeRangeDays ?? '365', 10);
  const months = Math.max(1, Math.ceil(requestedDays / 30));

  useEffect(() => {
    setLoading(true);
    const load = async () => {
      try {
        const response = await apiService.get(`/api/admin/subscriptions/intelligence/cohorts-expanded?months=${months}`);
        if (response.data) {
          setData(response.data as CohortData);
        }
      } catch (e) {
        console.error('Failed to load cohorts:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [months]);

  if (loading) {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', color: '#6B7280' }}>
          Unable to load cohort data
        </Typography>
      </Box>
    );
  }

  const maxOffset = Math.max(...data.matrix.flatMap(m => Object.keys(m.months).map(Number)), 0);
  const offsets = Array.from({ length: Math.min(maxOffset + 1, 12) }, (_, i) => i);

  // Get unique tiers for filter
  const tiers = [...new Set(data.byTier.map(t => t.tier))].sort();

  // Filter tier cohorts
  const filteredTierCohorts = tierFilter === 'all'
    ? data.byTier
    : data.byTier.filter(t => t.tier === tierFilter);

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {/* Cohort Retention Heatmap */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
          Cohort Retention Heatmap
        </Typography>
        {/* The date filter is a range; a cohort is a calendar month. Saying so
            is the difference between a rounded answer and a wrong one. */}
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11.5px', color: '#6B7280', mb: 1.5 }}>
          {`Cohorts are whole calendar months, so this tab rounds the date filter up. Showing the last ${data.months ?? months} month${(data.months ?? months) === 1 ? '' : 's'}`}
          {requestedDays < (data.months ?? months) * 30 ? ' — a wider window than the filter above.' : '.'}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, color: '#6B7280', minWidth: 100, borderBottom: '2px solid #E5E7EB' }}>
                  Cohort
                </TableCell>
                <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, color: '#6B7280', textAlign: 'center', borderBottom: '2px solid #E5E7EB' }}>
                  Size
                </TableCell>
                {offsets.map(o => (
                  <TableCell key={o} align="center" sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, color: '#6B7280', borderBottom: '2px solid #E5E7EB', minWidth: 48 }}>
                    M{o}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.matrix.map(row => (
                <TableRow key={row.cohort}>
                  <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 600, color: '#374151' }}>
                    {formatCohort(row.cohort)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 600, color: '#374151' }}>
                    {row.cohortSize}
                  </TableCell>
                  {offsets.map(o => {
                    const val = row.months[o];
                    return (
                      <TableCell
                        key={o}
                        align="center"
                        sx={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: '11px',
                          fontWeight: 700,
                          bgcolor: val !== undefined ? heatmapColor(val) : '#F9FAFB',
                          color: val !== undefined ? heatmapTextColor(val) : '#D1D5DB',
                          border: '1px solid #E5E7EB',
                          py: 1,
                        }}
                      >
                        {val !== undefined ? `${val}%` : '\u2014'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

              {/* Industry Benchmark Row */}
              <TableRow>
                <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, color: '#9CA3AF', fontStyle: 'italic' }}>
                  SaaS Benchmark
                </TableCell>
                <TableCell align="center" sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#9CA3AF' }}>
                  \u2014
                </TableCell>
                {offsets.map(o => (
                  <TableCell
                    key={o}
                    align="center"
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: '10px',
                      fontWeight: 600,
                      color: '#9CA3AF',
                      bgcolor: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      py: 0.75,
                    }}
                  >
                    {INDUSTRY_BENCHMARK[o] !== undefined ? `${INDUSTRY_BENCHMARK[o]}%` : ''}
                  </TableCell>
                ))}
              </TableRow>

              {/* LifeScribe Target Row */}
              <TableRow>
                <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, color: '#1A5F7A', fontStyle: 'italic' }}>
                  LifeScribe Target
                </TableCell>
                <TableCell align="center" sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#1A5F7A' }}>
                  \u2014
                </TableCell>
                {offsets.map(o => (
                  <TableCell
                    key={o}
                    align="center"
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#1A5F7A',
                      bgcolor: '#EFF6FF',
                      border: '1px solid #E5E7EB',
                      py: 0.75,
                    }}
                  >
                    {LIFESCRIBE_TARGET[o] !== undefined ? `${LIFESCRIBE_TARGET[o]}%` : ''}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Cohort by Tier */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827' }}>
            Cohort Retention by Tier
          </Typography>
          <Select
            size="small"
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', minWidth: 120, '& .MuiSelect-select': { py: 0.6 } }}
          >
            <MenuItem value="all">All Tiers</MenuItem>
            {tiers.map(t => (
              <MenuItem key={t} value={t}>{t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</MenuItem>
            ))}
          </Select>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Cohort', 'Tier', 'Size', 'Active', 'Retention'].map(h => (
                  <TableCell key={h} sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', borderBottom: '2px solid #E5E7EB' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTierCohorts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#6B7280', textAlign: 'center', py: 3 }}>
                    No cohort data for selected tier
                  </TableCell>
                </TableRow>
              ) : (
                filteredTierCohorts.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 600 }}>
                      {formatCohort(row.cohort)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6B7280' }}>
                      {row.tier.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px' }}>
                      {row.cohortSize}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px' }}>
                      {row.stillActive}
                    </TableCell>
                    <Tooltip
                      title={!row.isSufficient ? `Insufficient cohort data — needs ≥30 subscribers for statistical significance. Current cohort size: ${row.cohortSize}.` : ''}
                      arrow
                    >
                      <TableCell sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: '11px',
                        fontWeight: 700,
                        color: !row.isSufficient ? '#9CA3AF' : (row.retentionPct ?? 0) >= 80 ? '#10B981' : (row.retentionPct ?? 0) >= 50 ? '#F59E0B' : '#EF4444',
                        cursor: !row.isSufficient ? 'help' : 'default',
                      }}>
                        {row.isSufficient && row.retentionPct !== null ? `${row.retentionPct}%` : '—'}
                      </TableCell>
                    </Tooltip>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default CohortsExpandedTab;
